/** Admin: per-driver documents & contracts (view / expiry / upload). */
import { useEffect, useRef, useState } from "react";
import { FileText, Eye, Upload, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_UI = {
  valid: { label: "Valjan", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" },
  expiring: { label: "Uskoro istječe", cls: "bg-amber-500/15 border-amber-500/40 text-amber-400" },
  expired: { label: "Istekao", cls: "bg-red-500/15 border-red-500/40 text-red-400" },
  none: { label: "Bez roka", cls: "bg-white/5 border-white/15 text-zinc-500" },
};

const fmtSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("hr-HR") : "—");

export const DriverDocumentsSection = ({ driverId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newExpiry, setNewExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const addFileRef = useRef(null);
  const replaceRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/drivers/${driverId}/documents`);
      setFiles(data.files || []);
    } catch {
      toast.error("Učitavanje dokumenata nije uspjelo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (driverId) load(); }, [driverId]); // eslint-disable-line

  const view = async (f) => {
    try {
      const resp = await api.get(`/admin/drivers/${driverId}/documents/${f.idx}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank");
    } catch {
      toast.error("Otvaranje dokumenta nije uspjelo.");
    }
  };

  const saveExpiry = async (f, value) => {
    try {
      const { data } = await api.patch(`/admin/drivers/${driverId}/documents/${f.idx}`, { expiry_date: value || "" });
      setFiles(data.files || []);
      toast.success(value ? `Rok za "${f.label}" spremljen.` : `Rok za "${f.label}" uklonjen.`);
    } catch {
      toast.error("Spremanje roka nije uspjelo.");
    }
  };

  const upload = async (file, { slot, label, expiry } = {}) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (slot) fd.append("slot", slot);
      if (label) fd.append("label", label);
      if (expiry) fd.append("expiry_date", expiry);
      const { data } = await api.post(`/admin/drivers/${driverId}/documents/upload`, fd);
      setFiles(data.files || []);
      toast.success("Dokument učitan.");
      setNewLabel(""); setNewExpiry("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Učitavanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d0d] p-5" data-testid="driver-documents-section">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-4 h-4 text-[#C5A059]" />
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Dokumenti i ugovori</p>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">Učitavanje…</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-zinc-500 mb-4">Nema učitanih dokumenata za ovog vozača.</p>
      ) : (
        <div className="divide-y divide-white/5 mb-4">
          {files.map((f) => {
            const st = STATUS_UI[f.status] || STATUS_UI.none;
            return (
              <div key={f.idx} className="py-3 flex flex-wrap items-center gap-3" data-testid={`doc-row-${f.idx}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-white">{f.label}</p>
                  <p className="text-[11px] text-zinc-500">
                    {fmtDate(f.uploaded_at)} · {fmtSize(f.size)}
                    {f.signed_by && <span className="text-emerald-500/80"> · potpisao {f.signed_by}</span>}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${st.cls}`}
                      data-testid={`doc-status-${f.idx}`}>
                  {st.label}{f.days_left !== null && f.status === "expiring" ? ` · ${f.days_left}d` : ""}
                </span>
                <input
                  type="date"
                  defaultValue={f.expiry_date || ""}
                  onBlur={(e) => e.target.value !== (f.expiry_date || "") && saveExpiry(f, e.target.value)}
                  className="bg-black/40 border border-white/10 px-2 py-1 rounded-lg text-xs text-zinc-300 focus:outline-none focus:border-[#C5A059]/60"
                  title="Datum isteka"
                  data-testid={`doc-expiry-${f.idx}`}
                />
                <button type="button" onClick={() => view(f)}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-[#C5A059] hover:bg-white/5 transition"
                        title="Pregledaj" data-testid={`doc-view-${f.idx}`}>
                  <Eye className="w-4 h-4" />
                </button>
                <button type="button"
                        onClick={() => replaceRefs.current[f.idx]?.click()}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-[#C5A059] hover:bg-white/5 transition"
                        title="Učitaj novu verziju" data-testid={`doc-replace-${f.idx}`}>
                  <Upload className="w-4 h-4" />
                </button>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden"
                       ref={(el) => { replaceRefs.current[f.idx] = el; }}
                       onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], { slot: f.slot })} />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
        <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
               placeholder="Naziv novog dokumenta (npr. Taxi dozvola)"
               className="flex-1 min-w-[200px] bg-black/40 border border-white/10 px-3 py-1.5 rounded-lg text-xs focus:outline-none focus:border-[#C5A059]/60"
               data-testid="doc-new-label" />
        <input type="date" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)}
               className="bg-black/40 border border-white/10 px-2 py-1.5 rounded-lg text-xs text-zinc-300 focus:outline-none focus:border-[#C5A059]/60"
               title="Datum isteka (opcionalno)" data-testid="doc-new-expiry" />
        <button type="button" disabled={busy}
                onClick={() => newLabel.trim() ? addFileRef.current?.click() : toast.error("Unesite naziv dokumenta.")}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#C5A059] text-black text-xs font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
                data-testid="doc-add-btn">
          <Plus className="w-3.5 h-3.5" /> {busy ? "Učitavanje…" : "Dodaj dokument"}
        </button>
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden" ref={addFileRef}
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], { label: newLabel.trim(), expiry: newExpiry })} />
      </div>
    </div>
  );
};
